import type {
  AssertionDefinition,
  BlockDefinition,
  DerivedValueDefinition,
  Diagnostic,
  EdgeDefinition,
  EdgeListEntry,
  IncludedModule,
  MarkDefinition,
  MarkListEntry,
  ModuleAnchorDefinition,
  ModuleDefinition,
  ModuleRegistry,
  ModuleUse,
  OptionDefinition,
  PatternSpecification,
  PieceDefinition,
  PointOperation,
  PointReference,
  SeamComparisonDefinition,
  TransformDefinition,
  ValueReference,
} from "./types";

const ANCHOR_KINDS = new Set(["point", "edge", "mark", "piece", "value"]);
/** Prefixes are joined to module IDs with "_", so they hold no underscore themselves. */
const PREFIX = /^[A-Za-z][A-Za-z0-9]*$/;
const IDENTIFIER_PATH = /[A-Za-z_][A-Za-z0-9_]*(?:\.[A-Za-z_][A-Za-z0-9_]*)*/g;

/** Point operation fields that name one point, a pair of points, or a value. */
const POINT_FIELDS = ["from", "to", "xFrom", "yFrom", "around"];
const POINT_PAIR_FIELDS = ["between", "relativeTo", "axis", "lengthFrom"];
const VALUE_FIELDS = ["x", "y", "distance", "value", "angle"];

/** Check a parsed module file's own shape; its content is checked once merged into a host. */
export function validateModule(value: unknown): Diagnostic[] {
  const diagnostics: Diagnostic[] = [];
  const error = (code: string, message: string) => diagnostics.push({ severity: "error", code, message });
  if (!value || typeof value !== "object") return [{ severity: "error", code: "MODULE_OBJECT", message: "The module YAML root must be an object." }];
  const module = value as Partial<ModuleDefinition>;
  if (!module.module?.id || !module.module.name || !module.module.version) error("MODULE_METADATA", "Module id, name, and version are required.");
  const name = module.module?.id ?? "module";
  for (const [anchor, definition] of Object.entries(module.anchors ?? {})) {
    if (!definition || typeof definition !== "object" || !ANCHOR_KINDS.has(definition.kind)) {
      error("MODULE_ANCHOR", `${name} anchor ${anchor} needs kind: point, edge, mark, piece or value.`);
    }
  }
  for (const key of ["blocks", "measurements", "presets", "modules"]) {
    if ((module as Record<string, unknown>)[key] !== undefined) error("MODULE_DEFINITION", `${name}: modules cannot declare ${key}.`);
  }
  return diagnostics;
}

/**
 * Merge every `modules:` use of a raw (parsed, unvalidated) specification into
 * its host block, returning a flat specification without `modules`.
 *
 * IDs the module declares (points, edges, outline parts, marks, pieces,
 * derived values, constants, seam comparisons, assertions) become
 * `<prefix>_<id>`; anchor names become the host IDs bound to them; options
 * keep their IDs and merge into the host's, with the host's overrides.
 */
export function resolveModules(value: unknown, registry: ModuleRegistry = {}): { value: unknown; diagnostics: Diagnostic[] } {
  const diagnostics: Diagnostic[] = [];
  if (!value || typeof value !== "object") return { value, diagnostics };
  const raw = value as Partial<PatternSpecification> & { modules?: unknown };
  if (raw.modules === undefined) return { value, diagnostics };
  const spec = structuredClone(raw) as Partial<PatternSpecification> & { modules?: unknown };
  const uses = spec.modules;
  delete spec.modules;
  const error = (code: string, message: string) => diagnostics.push({ severity: "error", code, message });
  if (!Array.isArray(uses)) {
    error("MODULE_USE", "modules must be a list of { use, block, prefix, anchors }.");
    return { value: spec, diagnostics };
  }
  const included: IncludedModule[] = [];
  const prefixes = new Set<string>();
  uses.forEach((use: Partial<ModuleUse>, index) => {
    const label = `modules[${index}]${use?.use ? ` (${use.use})` : ""}`;
    if (!use || typeof use !== "object" || typeof use.use !== "string") {
      error("MODULE_USE", `${label} needs use: the module id.`);
      return;
    }
    const module = registry[use.use];
    if (!module) {
      const known = Object.keys(registry);
      error("MODULE_UNKNOWN", `${label} uses unknown module ${use.use}${known.length ? `; known modules: ${known.join(", ")}` : ""}.`);
      return;
    }
    if (typeof use.prefix !== "string" || !PREFIX.test(use.prefix)) {
      error("MODULE_PREFIX", `${label} needs a prefix of letters and digits, such as FO.`);
      return;
    }
    if (prefixes.has(use.prefix)) {
      error("MODULE_PREFIX", `${label} reuses prefix ${use.prefix}; each module use needs its own.`);
      return;
    }
    if (use.when !== undefined && typeof use.when !== "string" && typeof use.when !== "number") {
      error("MODULE_USE", `${label} when must be an expression.`);
      return;
    }
    prefixes.add(use.prefix);
    const block = typeof use.block === "string" ? spec.blocks?.[use.block] : undefined;
    if (!block) {
      error("MODULE_BLOCK", `${label} merges into unknown block ${String(use.block)}.`);
      return;
    }
    const before = diagnostics.length;
    const merged = new ModuleMerger(module, use as ModuleUse, spec, block, diagnostics).merge();
    if (merged && diagnostics.length === before) {
      included.push({ id: module.module.id, version: module.module.version, name: module.module.name, prefix: use.prefix, block: use.block! });
    }
  });
  if (included.length > 0) spec.includedModules = [...(spec.includedModules ?? []), ...included];
  return { value: spec, diagnostics };
}

class ModuleMerger {
  private readonly id: string;
  private readonly prefix: string;
  private readonly anchors: Record<string, ModuleAnchorDefinition>;
  private readonly bindings: Record<string, string | string[] | number | ValueReference>;
  private readonly local: Record<"point" | "edge" | "part" | "mark" | "markPart" | "piece" | "derived" | "constant" | "transform", Set<string>>;

  constructor(
    private readonly module: ModuleDefinition,
    private readonly use: ModuleUse,
    private readonly spec: Partial<PatternSpecification>,
    private readonly block: BlockDefinition,
    private readonly diagnostics: Diagnostic[],
  ) {
    this.id = module.module.id;
    this.prefix = use.prefix;
    this.anchors = module.anchors ?? {};
    this.bindings = use.anchors ?? {};
    const derived = new Set<string>();
    for (const [id, definition] of Object.entries(module.derivedValues ?? {})) {
      derived.add(id);
      if (definition?.operation === "pointAlongEdges" || definition?.operation === "edgeCrossing") { derived.add(`${id}X`); derived.add(`${id}Y`); }
    }
    this.local = {
      point: new Set((module.operations ?? []).map((operation) => operation?.id).filter((id): id is string => typeof id === "string")),
      edge: new Set(Object.keys(module.edges ?? {})),
      part: new Set(Object.keys(module.outlineParts ?? {})),
      mark: new Set((module.marks ?? []).map((mark) => mark?.id).filter((id): id is string => typeof id === "string")),
      markPart: new Set(Object.keys(module.markParts ?? {})),
      piece: new Set(Object.keys(module.pieces ?? {})),
      derived,
      constant: new Set(Object.keys(module.constants ?? {})),
      transform: new Set(Object.keys(module.transforms ?? {})),
    };
    // A transform's copies (<id><suffix>) of module points and edges are module
    // IDs too, so the module can name them. Copies of anchors are not.
    for (const transform of Object.values(module.transforms ?? {})) {
      if (!transform || typeof transform.suffix !== "string") continue;
      const carried = new Set(transform.points ?? []);
      for (const edgeId of transform.edges ?? []) {
        const edge = module.edges?.[edgeId];
        if (edge) [edge.from, edge.to].forEach((id) => carried.add(id));
        if (this.local.edge.has(edgeId)) this.local.edge.add(`${edgeId}${transform.suffix}`);
      }
      for (const id of carried) if (this.local.point.has(id) && !Object.hasOwn(transform.meets ?? {}, id)) this.local.point.add(`${id}${transform.suffix}`);
    }
  }

  private error(code: string, message: string) {
    this.diagnostics.push({ severity: "error", code, message: `Module ${this.id} (${this.prefix}): ${message}` });
  }

  merge(): boolean {
    if (!this.checkAnchors()) return false;
    const module = this.module;
    const block = this.block;
    const spec = this.spec;
    const hostPoints = new Set(Object.values(spec.blocks ?? {}).flatMap((candidate) => (candidate?.operations ?? []).map((operation) => operation?.id)));

    // Options keep their IDs; the host may override how they are offered. A
    // module used twice (under different prefixes) shares its options, so an
    // option already defined exactly as this use defines it is not a collision.
    const options = this.options();
    for (const id of Object.keys(options)) {
      if (spec.options?.[id] && JSON.stringify(spec.options[id]) !== JSON.stringify(options[id])) this.error("MODULE_COLLISION", `option ${id} is already defined by the pattern.`);
    }
    const constants = Object.fromEntries(Object.entries(module.constants ?? {}).map(([id, value]) => [this.named(id), value]));
    const operations = (module.operations ?? []).map((operation) => this.operation(operation));
    const edges = Object.fromEntries(Object.entries(module.edges ?? {}).map(([id, edge]) => [this.named(id), this.edge(edge)]));
    const outlineParts = Object.fromEntries(Object.entries(module.outlineParts ?? {}).map(([id, part]) => [this.named(id), this.edgeList(part)]));
    const marks = (module.marks ?? []).map((mark) => this.mark(mark));
    const markParts = Object.fromEntries(Object.entries(module.markParts ?? {}).map(([id, part]) => [this.named(id), this.markList(part)]));
    const pieces = Object.fromEntries(Object.entries(module.pieces ?? {}).map(([id, piece]) => [this.named(id), this.gate(this.piece(piece))]));
    const derivedValues = Object.fromEntries(Object.entries(module.derivedValues ?? {}).map(([id, definition]) => [this.named(id), this.derived(definition)]));
    const transforms = Object.fromEntries(Object.entries(module.transforms ?? {}).map(([id, definition]) => [this.named(id), this.transform(definition)]));
    const seamComparisons = Object.fromEntries(Object.entries(module.seamComparisons ?? {}).map(([id, comparison]) => [this.named(id), this.gate(this.seamComparison(comparison))]));
    const assertions = (module.assertions ?? []).map((assertion) => this.gate(this.assertion(assertion)));

    const collide = (kind: string, ids: Iterable<string>, existing: (id: string) => boolean) => {
      for (const id of ids) if (existing(id)) this.error("MODULE_COLLISION", `${kind} ${id} is already defined by the pattern.`);
    };
    collide("constant", Object.keys(constants), (id) => Object.hasOwn(spec.constants ?? {}, id));
    collide("point", operations.map((operation) => operation.id), (id) => hostPoints.has(id));
    collide("edge", Object.keys(edges), (id) => !!block.edges?.[id]);
    collide("outline part", Object.keys(outlineParts), (id) => !!block.outlineParts?.[id]);
    collide("mark", marks.map((mark) => mark.id), (id) => (block.marks ?? []).some((mark) => mark?.id === id));
    collide("mark part", Object.keys(markParts), (id) => !!block.markParts?.[id]);
    collide("piece", Object.keys(pieces), (id) => !!block.pieces?.[id]);
    collide("transform", Object.keys(transforms), (id) => !!block.transforms?.[id]);
    collide("derived value", Object.keys(derivedValues), (id) => Object.values(spec.blocks ?? {}).some((candidate) => !!candidate?.derivedValues?.[id]));
    collide("seam comparison", Object.keys(seamComparisons), (id) => !!spec.seamComparisons?.[id]);
    collide("assertion", assertions.map((assertion) => assertion.id), (id) => (spec.assertions ?? []).some((assertion) => assertion?.id === id));

    spec.options = { ...spec.options, ...options };
    spec.constants = { ...spec.constants, ...constants };
    block.operations = [...(Array.isArray(block.operations) ? block.operations : []), ...operations];
    if (Object.keys(edges).length) block.edges = { ...block.edges, ...edges };
    if (Object.keys(outlineParts).length) block.outlineParts = { ...block.outlineParts, ...outlineParts };
    if (marks.length) block.marks = [...(block.marks ?? []), ...marks];
    if (Object.keys(markParts).length) block.markParts = { ...block.markParts, ...markParts };
    if (Object.keys(pieces).length) block.pieces = { ...block.pieces, ...pieces };
    if (Object.keys(transforms).length) block.transforms = { ...block.transforms, ...transforms };
    if (Object.keys(derivedValues).length) block.derivedValues = { ...block.derivedValues, ...derivedValues };
    if (Object.keys(seamComparisons).length) spec.seamComparisons = { ...spec.seamComparisons, ...seamComparisons };
    if (assertions.length) spec.assertions = [...(spec.assertions ?? []), ...assertions];
    return true;
  }

  /** Every declared anchor is bound, to something of the right kind that the host has. */
  private checkAnchors(): boolean {
    let ok = true;
    const block = this.block;
    const hostPoints = new Set(Object.values(this.spec.blocks ?? {}).flatMap((candidate) => (candidate?.operations ?? []).map((operation) => operation?.id)));
    for (const [name, anchor] of Object.entries(this.anchors)) {
      const binding = this.bindings[name];
      const about = anchor?.description ? ` (${anchor.description})` : "";
      if (binding === undefined) {
        this.error("MODULE_ANCHOR_MISSING", `anchor ${name}${about} is not bound; add it under anchors.`);
        ok = false;
        continue;
      }
      for (const [kind, ids] of Object.entries(this.local)) {
        if (kind === anchor.kind && ids.has(name)) this.error("MODULE_DEFINITION", `anchor ${name} has the same name as a module ${kind}.`);
      }
      if (anchor.kind === "value") {
        if (typeof binding !== "number" && typeof binding !== "string" && (!binding || typeof binding !== "object")) {
          this.error("MODULE_ANCHOR_TARGET", `value anchor ${name} must be bound to a number, an expression or a value reference.`);
          ok = false;
        }
        continue;
      }
      // An edge anchor may name several edges that chain end to end.
      const targets = anchor.kind === "edge" && Array.isArray(binding) ? binding : [binding];
      if (targets.length === 0 || targets.some((target) => typeof target !== "string" || target === "")) {
        this.error("MODULE_ANCHOR_TARGET", `${anchor.kind} anchor ${name} must be bound to a ${anchor.kind} ID${anchor.kind === "edge" ? " or a list of edge IDs" : ""}.`);
        ok = false;
        continue;
      }
      for (const target of targets as string[]) {
        const exists = anchor.kind === "point" ? hostPoints.has(target)
          : anchor.kind === "edge" ? !!block.edges?.[target]
            : anchor.kind === "mark" ? (block.marks ?? []).some((mark) => mark?.id === target)
              : !!block.pieces?.[target];
        if (!exists) {
          this.error("MODULE_ANCHOR_TARGET", `anchor ${name} is bound to ${target}, which is not a ${anchor.kind}${anchor.kind === "point" ? "" : ` of block ${this.use.block}`}.`);
          ok = false;
        }
      }
    }
    for (const name of Object.keys(this.bindings)) {
      if (!this.anchors[name]) {
        this.error("MODULE_ANCHOR_UNKNOWN", `binds unknown anchor ${name}; the module declares ${Object.keys(this.anchors).join(", ") || "no anchors"}.`);
        ok = false;
      }
    }
    return ok;
  }

  private options(): Record<string, OptionDefinition> {
    const result: Record<string, OptionDefinition> = structuredClone(this.module.options ?? {});
    for (const [id, override] of Object.entries(this.use.options ?? {})) {
      const definition = result[id];
      if (!definition) {
        this.error("MODULE_OPTION_UNKNOWN", `overrides unknown option ${id}.`);
        continue;
      }
      const { choices, ...rest } = override ?? {};
      Object.assign(definition, rest);
      if (choices !== undefined) {
        if (definition.type !== "choice") {
          this.error("MODULE_OPTION", `option ${id} is not a choice, so its choices cannot be overridden.`);
          continue;
        }
        const keys = Array.isArray(choices) ? choices : Object.keys(choices);
        const unknown = keys.filter((key) => !Object.hasOwn(definition.choices, key));
        if (unknown.length) this.error("MODULE_OPTION", `option ${id} offers unknown choices ${unknown.join(", ")}.`);
        definition.choices = Object.fromEntries(keys.filter((key) => Object.hasOwn(definition.choices, key)).map((key) => [key, Array.isArray(choices) ? definition.choices[key] : choices[key]]));
      }
    }
    for (const id of this.use.hideOptions ?? []) {
      if (!result[id]) this.error("MODULE_OPTION_UNKNOWN", `hides unknown option ${id}.`);
      else result[id].hidden = true;
    }
    return result;
  }

  private named(id: string): string {
    return `${this.prefix}_${id}`;
  }

  private ref(kind: "point" | "edge" | "mark" | "piece", name: unknown): string {
    if (typeof name !== "string") return name as string;
    if (this.anchors[name]?.kind === kind) {
      const binding = this.bindings[name];
      if (Array.isArray(binding)) {
        this.error("MODULE_ANCHOR_TARGET", `anchor ${name} is bound to several edges, but is used where only one edge can go.`);
        return binding[0];
      }
      return binding as string;
    }
    return this.local[kind].has(name) ? this.named(name) : name;
  }

  private point = (name: unknown) => this.ref("point", name);
  private edgeRef = (name: unknown) => this.ref("edge", name);

  /** An edge name where a list can go: an edge anchor bound to several edges expands to all of them. */
  private edgeRefs = (name: unknown): string[] => {
    if (typeof name === "string" && this.anchors[name]?.kind === "edge" && Array.isArray(this.bindings[name])) return [...this.bindings[name] as string[]];
    return [this.edgeRef(name)];
  };

  private pair(pair: unknown): [string, string] {
    return Array.isArray(pair) ? pair.map(this.point) as [string, string] : pair as [string, string];
  }

  /** Bare edge IDs in seam comparisons and assertions are qualified with the host block. */
  private qualified(name: string): string[] {
    return name.includes(".") ? [name] : this.edgeRefs(name).map((edge) => `${this.use.block}.${edge}`);
  }

  private value(reference: unknown): ValueReference {
    if (!reference || typeof reference !== "object") return reference as ValueReference;
    const input = reference as Record<string, unknown>;
    if (typeof input.anchor === "string") return this.valueBinding(input.anchor);
    if (typeof input.constant === "string" && this.local.constant.has(input.constant)) return { constant: this.named(input.constant) };
    if (typeof input.derived === "string" && this.local.derived.has(input.derived)) return { derived: this.named(input.derived) };
    if (typeof input.expression === "string") return { expression: this.expression(input.expression) };
    return { ...input } as ValueReference;
  }

  private valueBinding(name: string): ValueReference {
    const binding = this.anchors[name]?.kind === "value" ? this.bindings[name] : undefined;
    if (binding === undefined) {
      this.error("MODULE_ANCHOR_UNKNOWN", `reads value anchor ${name}, which it does not declare.`);
      return { anchor: name } as unknown as ValueReference;
    }
    if (typeof binding === "number") return binding;
    if (typeof binding === "string") return { expression: binding };
    return { ...binding as object } as ValueReference;
  }

  /** Rename module IDs and anchors inside an expression; options and measurements read through unchanged. */
  private expression(source: string): string {
    if (typeof source !== "string") return source;
    return source.replace(IDENTIFIER_PATH, (path) => {
      const [namespace, name, ...rest] = path.split(".");
      if (name === undefined) return path;
      if (namespace === "anchors" && rest.length === 0) return `(${expressionText(this.valueBinding(name))})`;
      if (namespace === "points") return ["points", this.point(name), ...rest].join(".");
      if (namespace === "constants" && this.local.constant.has(name)) return ["constants", this.named(name), ...rest].join(".");
      if (namespace === "derived" && this.local.derived.has(name)) return ["derived", this.named(name), ...rest].join(".");
      return path;
    });
  }

  /** Switch a piece, seam comparison or assertion off with the use's own `when` (a host expression, not renamed). */
  private gate<T extends { when?: string }>(item: T): T {
    const when = this.use.when;
    if (when === undefined || !item || typeof item !== "object") return item;
    return { ...item, when: item.when === undefined ? String(when) : `and(${when}, ${item.when})` };
  }

  private when<T extends { when?: string }>(item: T): T {
    return item.when === undefined ? item : { ...item, when: this.expression(item.when) };
  }

  private operation(operation: PointOperation): PointOperation {
    const result: Record<string, unknown> = { ...operation, id: this.named(operation.id) };
    for (const field of POINT_FIELDS) if (typeof result[field] === "string") result[field] = this.point(result[field]);
    for (const field of POINT_PAIR_FIELDS) if (Array.isArray(result[field])) result[field] = this.pair(result[field]);
    for (const field of VALUE_FIELDS) if (result[field] !== undefined && typeof result[field] !== "string") result[field] = this.value(result[field]);
    const guide = result.towardConstructionLine as Record<string, unknown> | undefined;
    if (guide && typeof guide === "object") result.towardConstructionLine = { ...guide, from: this.point(guide.from), ...(guide.offset !== undefined ? { offset: this.value(guide.offset) } : {}) };
    const opening = result.opening as Record<string, unknown> | undefined;
    if (opening && typeof opening === "object") result.opening = { ...opening, point: this.point(opening.point), ...(opening.distance !== undefined ? { distance: this.value(opening.distance) } : {}) };
    if (typeof result.transform === "string" && this.local.transform.has(result.transform)) result.transform = this.named(result.transform);
    const source = typeof operation.source === "string" ? operation.source : operation.label ?? operation.id;
    result.source = `${this.id}: ${source}`;
    return result as PointOperation;
  }

  private controlPoint(control: PointReference | undefined): PointReference | undefined {
    if (typeof control === "string") return this.point(control);
    if (control && !Array.isArray(control) && typeof control === "object" && control.point !== undefined) return { ...control, point: this.point(control.point) };
    return control;
  }

  private edge(edge: EdgeDefinition): EdgeDefinition {
    if (!edge || typeof edge !== "object") return edge;
    const result: EdgeDefinition = this.when({ ...edge, from: this.point(edge.from), to: this.point(edge.to) });
    if (edge.through) result.through = edge.through.map(this.point);
    if (edge.edge !== undefined) {
      // A portion of an anchor bound to several edges takes the stretch from all of them.
      const sources = this.edgeRefs(edge.edge);
      if (sources.length > 1 && edge.operation === "edgePortion") {
        delete result.edge;
        result.edges = sources;
      } else {
        result.edge = this.edgeRef(edge.edge);
      }
    }
    if (edge.edges) result.edges = edge.edges.flatMap(this.edgeRefs);
    if (edge.axis) result.axis = this.pair(edge.axis);
    if (edge.transform !== undefined && this.local.transform.has(edge.transform)) result.transform = this.named(edge.transform);
    if (edge.toward !== undefined) result.toward = this.point(edge.toward);
    if (edge.touch !== undefined) result.touch = this.point(edge.touch);
    if (edge.control1 !== undefined) result.control1 = this.controlPoint(edge.control1);
    if (edge.control2 !== undefined) result.control2 = this.controlPoint(edge.control2);
    if (edge.constraints) {
      result.constraints = { ...edge.constraints };
      if (edge.constraints.startAlong) result.constraints.startAlong = this.pair(edge.constraints.startAlong);
      if (edge.constraints.endAlong) result.constraints.endAlong = this.pair(edge.constraints.endAlong);
    }
    return result;
  }

  private edgeList(list: EdgeListEntry[] | undefined): EdgeListEntry[] {
    if (!Array.isArray(list)) return list as unknown as EdgeListEntry[];
    return list.flatMap((entry): EdgeListEntry[] => {
      if (typeof entry === "string") return this.edgeRefs(entry);
      if (entry && typeof entry === "object" && "part" in entry) return [{ part: this.local.part.has(entry.part) ? this.named(entry.part) : entry.part }];
      if (entry && typeof entry === "object" && "edges" in entry) return [{ when: this.expression(entry.when), edges: this.edgeList(entry.edges) }];
      return [entry];
    });
  }

  private markList(list: MarkListEntry[] | undefined): MarkListEntry[] {
    if (!Array.isArray(list)) return list as unknown as MarkListEntry[];
    return list.map((entry): MarkListEntry => {
      if (typeof entry === "string") return this.ref("mark", entry);
      if (entry && typeof entry === "object" && "part" in entry) return { part: this.local.markPart.has(entry.part) ? this.named(entry.part) : entry.part };
      if (entry && typeof entry === "object" && "marks" in entry) return { when: this.expression(entry.when), marks: this.markList(entry.marks) };
      return entry;
    });
  }

  private mark(mark: MarkDefinition): MarkDefinition {
    if (!mark || typeof mark !== "object") return mark;
    const result: MarkDefinition = this.when({ ...mark, id: this.named(mark.id) });
    if (mark.from !== undefined) result.from = this.point(mark.from);
    if (mark.to !== undefined) result.to = this.point(mark.to);
    if (mark.at !== undefined) result.at = this.point(mark.at);
    if (mark.along !== undefined) result.along = this.edgeRef(mark.along);
    return result;
  }

  private piece(piece: PieceDefinition): PieceDefinition {
    if (!piece || typeof piece !== "object") return piece;
    const result: PieceDefinition = this.when({ ...piece, outline: this.edgeList(piece.outline) });
    if (piece.layoutWith !== undefined) result.layoutWith = this.ref("piece", piece.layoutWith);
    if (piece.bodyOverlay) result.bodyOverlay = this.when({ ...piece.bodyOverlay, edges: this.edgeList(piece.bodyOverlay.edges) });
    if (piece.referenceOverlays) {
      result.referenceOverlays = piece.referenceOverlays.map((overlay) => this.when({
        ...overlay,
        edges: this.edgeList(overlay.edges),
        align: overlay.align && { from: this.pair(overlay.align.from), to: this.pair(overlay.align.to) },
      }));
    }
    if (piece.internalMarks) result.internalMarks = this.markList(piece.internalMarks);
    if (piece.internalLines) result.internalLines = this.edgeList(piece.internalLines);
    if (piece.seamAllowances) result.seamAllowances = piece.seamAllowances.map((rule) => this.when({ ...rule, edges: this.edgeList(rule.edges), distance: this.allowance(rule.distance) }));
    if (piece.inlays) result.inlays = piece.inlays.map((rule) => this.when({ ...rule, edges: this.edgeList(rule.edges), distance: this.allowance(rule.distance) }));
    if (piece.clips) result.clips = piece.clips.map((clip) => this.when({ ...clip, along: this.edgeList(clip.along), away: this.point(clip.away), ...(clip.allowance !== undefined ? { allowance: this.allowance(clip.allowance) } : {}) }));
    if (piece.print?.cutOnFold !== undefined) result.print = { ...piece.print, cutOnFold: this.edgeRef(piece.print.cutOnFold) };
    return result;
  }

  /** A seam allowance or inlay distance: a number, an option, or a value anchor bound to either. */
  private allowance(distance: number | { option: string } | { anchor: string }): number | { option: string } {
    if (!distance || typeof distance !== "object" || !("anchor" in distance)) return distance as number | { option: string };
    const value = this.valueBinding(distance.anchor);
    if (typeof value === "number") return value;
    if (typeof value === "object" && typeof value.option === "string" && Object.keys(value).length === 1) return { option: value.option };
    this.error("MODULE_ANCHOR_TARGET", `value anchor ${distance.anchor} sets a seam allowance or inlay, so it must be bound to a number or { option }.`);
    return 0;
  }

  private derived(definition: DerivedValueDefinition): DerivedValueDefinition {
    if (!definition || typeof definition !== "object") return definition;
    const result: DerivedValueDefinition = this.when({ ...definition });
    if (definition.edges) result.edges = definition.edges.flatMap(this.edgeRefs);
    if (definition.from !== undefined) result.from = this.point(definition.from);
    if (definition.distance !== undefined) result.distance = this.value(definition.distance);
    if (definition.x !== undefined) result.x = this.value(definition.x);
    if (definition.y !== undefined) result.y = this.value(definition.y);
    if (definition.crossing) result.crossing = definition.crossing.flatMap(this.edgeRefs);
    return result;
  }

  private transform(definition: TransformDefinition): TransformDefinition {
    if (!definition || typeof definition !== "object") return definition;
    const result: TransformDefinition = this.when({ ...definition });
    if (Array.isArray(definition.points)) result.points = definition.points.map(this.point);
    if (Array.isArray(definition.edges)) result.edges = definition.edges.flatMap(this.edgeRefs);
    if (definition.meets && typeof definition.meets === "object") result.meets = Object.fromEntries(Object.entries(definition.meets).map(([source, target]) => [this.point(source), this.point(target)]));
    if (definition.follow !== undefined) result.follow = this.local.transform.has(definition.follow) ? this.named(definition.follow) : definition.follow;
    const rotate = definition.rotate;
    if (rotate && typeof rotate === "object") {
      result.rotate = { ...rotate, around: this.point(rotate.around) };
      if (rotate.angle !== undefined) result.rotate.angle = this.value(rotate.angle);
      if (rotate.close !== undefined) result.rotate.close = this.pair(rotate.close);
      if (rotate.ratio !== undefined) result.rotate.ratio = this.value(rotate.ratio);
      if (rotate.opening && typeof rotate.opening === "object") result.rotate.opening = { ...rotate.opening, point: this.point(rotate.opening.point), distance: this.value(rotate.opening.distance) };
    }
    const align = definition.align;
    if (align && typeof align === "object") result.align = { from: this.pair(align.from), to: this.pair(align.to) };
    const translate = definition.translate;
    if (translate && typeof translate === "object") {
      result.translate = { ...translate };
      if (translate.along !== undefined) result.translate.along = this.pair(translate.along);
      for (const field of ["dx", "dy", "distance"] as const) if (translate[field] !== undefined) result.translate[field] = this.value(translate[field]);
    }
    return result;
  }

  private seamComparison(comparison: SeamComparisonDefinition): SeamComparisonDefinition {
    if (!comparison || typeof comparison !== "object") return comparison;
    const side = (length: SeamComparisonDefinition["first"]) => length && { ...length, edges: Array.isArray(length.edges) ? length.edges.flatMap((edge) => this.qualified(edge)) : length.edges };
    return this.when({ ...comparison, first: side(comparison.first), second: side(comparison.second) });
  }

  private assertion(assertion: AssertionDefinition): AssertionDefinition {
    if (!assertion || typeof assertion !== "object") return assertion;
    const result = this.when({ ...assertion, id: this.named(assertion.id), source: `${this.id}${assertion.source ? `: ${assertion.source}` : ""}` }) as AssertionDefinition;
    for (const bound of ["equals", "minimum", "maximum"] as const) if (assertion[bound] !== undefined) result[bound] = this.value(assertion[bound]);
    if (result.check === "distance") {
      result.from = this.point(result.from);
      result.to = this.point(result.to);
    } else if (result.check === "edgeLength" && Array.isArray(result.edges)) {
      result.edges = result.edges.flatMap((edge) => this.qualified(edge));
    }
    return result;
  }
}

/** A value reference written as an expression term. */
function expressionText(reference: ValueReference): string {
  if (typeof reference === "number") return String(reference);
  if (reference.measurement) return `measurements.${reference.measurement}`;
  if (reference.option) return `options.${reference.option}`;
  if (reference.constant) return `constants.${reference.constant}`;
  if (reference.derived) return `derived.${reference.derived}`;
  return reference.expression ?? "0";
}
